import React, { FC, PropsWithChildren, ReactNode, useState } from "react";
import { useMediaQuery } from "react-responsive";
import styled, { StyledComponentProps } from "styled-components";
import {
  Box,
  Button,
  HFlex,
  MenuAction,
  MenuItem,
  Navbar,
  NavbarCollapse,
  NavbarLogo,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuLink,
  NavbarProps,
  TooltipOnHover,
  tombac,
  useTombac,
} from "tombac";
import { ForwardIcon, WhatsNewIcon, CopyIcon } from "tombac-icons";
import { trackEvent } from "../../Tracking";
import { TrialBadge } from "../../components/Trial";
import { useLegoland } from "../LegolandContext";
import { useLogoutMutation } from "../authApi/authApiHooks";
import { useMeDetailsQuery, useUserProduct } from "../legolandApi";
import { useReleaseNotesStatus } from "../releaseNotes";
import { GuideButton } from "./GuideButton";
import { Dot, IconButton } from "./IconButton";
import { LegolandNavbarApps } from "./LegolandNavbarApps/LegolandNavbarApps";
import { LegolandNavbarLimits } from "./LegolandNavbarLimits";
import { LegolandNavbarMe } from "./LegolandNavbarMe";
import { TrackingFunction } from "./LegolandNavbarTypes";
import { LegolandNavbarApiKeyModal } from "./LegolandNavbarApiKeyModal";

export interface LegolandNavbarProps extends NavbarProps {
  /** Text displayed on the right hand side, usually to describe sign in/sign up button. Visible only for unauthenticated users. */
  authHintText?: ReactNode;
  /** URL to the user manual. */
  manualUrl?: string;
  /** Breakpoint at which the "me" info box and menu should collapse to the smaller version. */
  meMenuBreakpoint?: number;
  /** If provided, then the guided tour item is added to the user guide button. The callback is called when the user clicks on it. */
  onGuidedTour?: () => void;
  /** Overwrite the name displayed beneath the logo. If ommited, the product name will be derived from product id provided in LegolandApp. */
  productName?: ReactNode;
  /** Show sign in link when the user is unauthenticated? */
  showSignIn?: boolean;
  /** Show sign up link when the user is unauthenticated? */
  showSignUp?: boolean;
  /** Event category for tealium tracking. For example "od interaction". Required for enabling tracking.  */
  trackingCategory?: string;
}

const Container = styled(Navbar)`
  .TombacIcon {
    box-sizing: content-box;
    display: block;
    height: ${tombac.unit(24)};
    width: ${tombac.unit(24)};
  }
`;

const HintText = styled.span`
  ${tombac.altText({
    color: tombac.color("neutral", 600),
    fontSize: 14,
    fontWeight: 500,
  })}

  &:last-child {
    margin-right: ${tombac.space(3)};
  }
`;

const Subtitle = styled.span`
  align-items: center;
  display: flex;
  justify-content: baseline;
  white-space: nowrap;
`;

export const LegolandNavbar: FC<PropsWithChildren<LegolandNavbarProps>> = ({
  authHintText,
  breakpoint = 768,
  children,
  manualUrl,
  meMenuBreakpoint,
  onGuidedTour,
  productName,
  showSignIn = true,
  showSignUp = false,
  trackingCategory,
  ...rest
}) => {
  const isMaxSizeM = useMediaQuery({ maxWidth: breakpoint });

  const tombac = useTombac();
  const { legolandUiUrl, Link, releaseNotesUrl } = useLegoland();

  const releaseNotesStatus = useReleaseNotesStatus();
  const [isOpenApiKeyModal, setIsOpenApiKeyModal] = useState(false);

  const meDetailsQuery = useMeDetailsQuery();
  const { mutate: logout } = useLogoutMutation();

  const isAuthenticated =
    meDetailsQuery.isSuccess && meDetailsQuery.data.meDetails;

  const meProducts = meDetailsQuery?.data?.meDetails?.products;

  const isAnonymous = meDetailsQuery.isSuccess && !isAuthenticated;

  const userProduct = useUserProduct();
  productName = productName ?? userProduct?.displayName ?? "";

  const track: TrackingFunction = (action: string, label: string) => {
    if (!trackingCategory) {
      return;
    }
    trackEvent(trackingCategory, action, label);
  };

  //openModal function that adds styles from shadowroot - ONLY temporary solution!
  // const openModal = () => {
  //   setTimeout(() => {
  //     let styles = document
  //       .getElementsByClassName(
  //         "playgroundPreview_docs-site-src-theme-Playground-styles-module"
  //       )[0]
  //       .getElementsByTagName("div")[0]
  //       .shadowRoot?.querySelector("style");
  //     styles &&
  //       document.getElementsByTagName("head")[0].appendChild(styles as any);
  //   }, 1000);
  // };

  const meMenuLinkProps = isAuthenticated
    ? [
        !meDetailsQuery.data!.meDetails!.isFallback
          ? ({
              as: Link,
              children: "Settings",
              className: "LegolandNavbar__settings",
              href: `${legolandUiUrl}/settings`,
              onClick: () => {
                track("top navigation", "settings");
              },
            } as StyledComponentProps<any, any, {}, never>)
          : null,
        {
          as: "button" as const,
          children: "Show API key",
          className: "LegolandNavbar__apikeyModal",
          onClick: () => {
            // openModal(); // function that adds styles from shadowroot - ONLY temporary solution!
            setIsOpenApiKeyModal(true);
          },
        } as StyledComponentProps<any, any, {}, never>,
        {
          $color: tombac.color("danger", 500),
          as: "button" as const,
          children: "Sign out",
          className: "LegolandNavbar__sign-out",
          onClick: () => {
            track("top navigation", "logout");
            logout();
          },
        } as StyledComponentProps<any, any, {}, never>,
      ].filter(Boolean)
    : [];

  const iconButtons = [];

  if (releaseNotesUrl)
    iconButtons.push({
      icon: <WhatsNewIcon />,
      label: "What's new?",
      props: {
        as: Link,
        href: releaseNotesUrl,
        onClick: () => {
          track("top navigation", "release notes");
        },
      } as StyledComponentProps<any, any, {}, never>,
      showDot: releaseNotesStatus.isRead === false,
    });

  return (
    <Container {...{ breakpoint, ...rest }}>
      <NavbarLogo
        as="a"
        href="/"
        title={
          productName && (
            <Subtitle>
              {productName}{" "}
              {userProduct !== undefined &&
                !userProduct.isFallback &&
                userProduct.access.status !== "DENIED" &&
                userProduct.access.accessType === "EVALUATION" && (
                  <TrialBadge $ml="1sp" />
                )}
            </Subtitle>
          )
        }
      />
      {(children || isAuthenticated) && (
        <NavbarCollapse breakpoint={breakpoint}>
          {children}
          {isMaxSizeM ? (
            <>
              {(iconButtons.length > 0 || manualUrl || onGuidedTour) && (
                <NavbarMenu breakpoint={breakpoint}>
                  {iconButtons.map((button) => (
                    <NavbarMenuItem key={button.label}>
                      <NavbarMenuLink {...button.props}>
                        {button.label}
                        {button.showDot && <Dot breakpoint={breakpoint} />}
                      </NavbarMenuLink>
                    </NavbarMenuItem>
                  ))}
                  <GuideButton
                    asNavbarItems
                    manualUrl={manualUrl}
                    onGuidedTour={onGuidedTour}
                    track={track}
                  />
                </NavbarMenu>
              )}
              <NavbarMenu breakpoint={breakpoint}>
                {meMenuLinkProps.map((props) => (
                  <NavbarMenuItem key={props!.className}>
                    <NavbarMenuLink {...props} />
                  </NavbarMenuItem>
                ))}
              </NavbarMenu>
            </>
          ) : (
            <HFlex alignItems="center" h="100%" marginLeft="auto">
              <HFlex className="MoveNavbar__icon-buttons">
                <LegolandNavbarLimits track={track} />
                {iconButtons.map((button) => (
                  <Box $ml="1sp" key={button.label}>
                    <TooltipOnHover
                      content={button.label}
                      placement="bottom"
                      size="s"
                      variant="inverted"
                    >
                      <IconButton {...button.props}>
                        {button.icon}
                        {button.showDot && <Dot breakpoint={breakpoint} />}
                      </IconButton>
                    </TooltipOnHover>
                  </Box>
                ))}
                <Box $ml="1sp">
                  <GuideButton
                    manualUrl={manualUrl}
                    onGuidedTour={onGuidedTour}
                    track={track}
                  />
                </Box>
              </HFlex>
              <LegolandNavbarMe
                breakpoint={meMenuBreakpoint}
                menuItems={meMenuLinkProps.map((props) => (
                  <MenuItem key={props!.className}>
                    <MenuAction {...props} />
                  </MenuItem>
                ))}
              />
              <LegolandNavbarApps track={track} />
            </HFlex>
          )}
        </NavbarCollapse>
      )}
      {isAnonymous && (
        <Box
          $alignItems="baseline"
          $display="flex"
          $marginLeft="auto"
          $marginRight="3sp"
        >
          {authHintText && !isMaxSizeM && <HintText>{authHintText}</HintText>}
          {showSignIn && (
            <Button
              $marginLeft="3sp"
              append={<ForwardIcon />}
              appendedHoverEffect="anim-forward"
              as={Link}
              className="LegolandNavbar__sign-in"
              href={`${legolandUiUrl}/login${
                legolandUiUrl ? `?redirect=${window.location.toString()}` : ""
              }`}
              onClick={() => track("top navigation", "sign in")}
              variant="primary"
            >
              Log in
            </Button>
          )}
          {showSignUp && (
            <Button
              $marginLeft="3sp"
              append={<ForwardIcon />}
              appendedHoverEffect="anim-forward"
              as={Link}
              className="LegolandNavbar__sign-up"
              href={`${legolandUiUrl}/register`}
              onClick={() => track("top navigation", "sign up")}
              variant="primary"
            >
              Register
            </Button>
          )}
        </Box>
      )}
      <LegolandNavbarApiKeyModal
        isOpen={isOpenApiKeyModal}
        products={meProducts}
        setIsOpen={setIsOpenApiKeyModal}
      />
    </Container>
  );
};
